import { supabase } from '../firebase/config';

export interface ImageUploadResult {
  url: string;
  path: string;
  name: string;
  size: number;
  type: string;
}

export interface ProductImage {
  id: string;
  url: string;
  path: string;
  alt: string;
  isPrimary: boolean;
  order: number;
  size: number;
  type: string;
}

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Failed to read image file'));
        return;
      }
      resolve(result.split(',')[1] || '');
    };
    reader.onerror = () => reject(new Error('Failed to read image file'));
    reader.readAsDataURL(file);
  });

const inferContentType = (file: File): string => {
  if (file.type && file.type.startsWith('image/')) return file.type;
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  if (ext === 'png') return 'image/png';
  if (ext === 'webp') return 'image/webp';
  return file.type;
};

class ImageUploadService {
  private readonly maxFileSize = 4 * 1024 * 1024;
  private readonly allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  private readonly maxImagesPerProduct = 10;

  private async getAuthHeaders(): Promise<HeadersInit> {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) {
      throw new Error('You must be signed in to upload images');
    }
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  async uploadImage(
    file: File,
    productId: string,
    imageId: string,
    isPrimary: boolean = false,
    order: number = 0
  ): Promise<ImageUploadResult> {
    void isPrimary;
    void order;

    try {
      this.validateFile(file);
      const resizedFile = await this.resizeImage(file);
      const contentType = inferContentType(resizedFile);
      const headers = await this.getAuthHeaders();
      const fileData = await fileToBase64(resizedFile);

      const response = await fetch('/api/product-images', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          productId,
          fileName: file.name,
          contentType,
          fileSize: resizedFile.size,
          imageId,
          fileData,
        }),
      });

      const payload = (await response.json()) as {
        url?: string;
        path?: string;
        error?: string;
      };

      if (!response.ok || !payload.url || !payload.path) {
        throw new Error(payload.error || 'Failed to upload image');
      }

      return {
        url: payload.url,
        path: payload.path,
        name: file.name,
        size: resizedFile.size,
        type: contentType,
      };
    } catch (error) {
      console.error('Error uploading image:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to upload image');
    }
  }

  async uploadProductImages(files: File[], productId: string): Promise<ProductImage[]> {
    try {
      if (files.length > this.maxImagesPerProduct) {
        throw new Error(`Maximum ${this.maxImagesPerProduct} images allowed per product`);
      }

      const uploaded: ProductImage[] = [];
      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        const imageId = `img_${Date.now()}_${index}`;
        const isPrimary = index === 0 && uploaded.every((img) => !img.isPrimary);
        const result = await this.uploadImage(file, productId, imageId, isPrimary, index);
        uploaded.push({
          id: imageId,
          url: result.url,
          path: result.path,
          alt: file.name.split('.')[0],
          isPrimary,
          order: index,
          size: result.size,
          type: result.type,
        });
      }

      return uploaded;
    } catch (error) {
      console.error('Error uploading product images:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to upload product images');
    }
  }

  async deleteImage(imagePath: string, productId: string): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch('/api/product-images', {
        method: 'DELETE',
        headers,
        body: JSON.stringify({ path: imagePath, productId }),
      });
      const payload = (await response.json()) as { error?: string };
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to delete image');
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to delete image');
    }
  }

  async deleteProductImages(productId: string): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch('/api/product-images', {
        method: 'DELETE',
        headers,
        body: JSON.stringify({ productId, deleteAll: true }),
      });
      const payload = (await response.json()) as { error?: string };
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to delete product images');
      }
    } catch (error) {
      console.error('Error deleting product images:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to delete product images');
    }
  }

  async getProductImages(_productId: string): Promise<ProductImage[]> {
    return [];
  }

  async updateImageMetadata(
    productId: string,
    imageId: string,
    updates: {
      isPrimary?: boolean;
      order?: number;
      alt?: string;
    }
  ): Promise<void> {
    void productId;
    void imageId;
    void updates;
  }

  async resizeImage(file: File, maxWidth: number = 1200, maxHeight: number = 1200, quality: number = 0.8): Promise<File> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        let { width, height } = img;

        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }

        canvas.width = width;
        canvas.height = height;
        ctx?.drawImage(img, 0, 0, width, height);

        const outputType = inferContentType(file) || 'image/jpeg';
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(
                new File([blob], file.name, {
                  type: outputType,
                  lastModified: Date.now(),
                })
              );
            } else {
              reject(new Error('Failed to resize image'));
            }
          },
          outputType,
          quality
        );
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  }

  async generateThumbnail(file: File, size: number = 300): Promise<File> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        let { width, height } = img;

        if (width > height) {
          height = (height * size) / width;
          width = size;
        } else {
          width = (width * size) / height;
          height = size;
        }

        canvas.width = size;
        canvas.height = size;

        const x = (size - width) / 2;
        const y = (size - height) / 2;
        ctx?.drawImage(img, x, y, width, height);

        const outputType = inferContentType(file) || 'image/jpeg';
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(
                new File([blob], `thumb_${file.name}`, {
                  type: outputType,
                  lastModified: Date.now(),
                })
              );
            } else {
              reject(new Error('Failed to generate thumbnail'));
            }
          },
          outputType,
          0.8
        );
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  }

  private validateFile(file: File): void {
    const contentType = inferContentType(file);
    if (!this.allowedTypes.includes(contentType)) {
      throw new Error(`File type is not allowed. Use JPEG, PNG, or WebP. (${file.name})`);
    }

    if (file.size > this.maxFileSize) {
      throw new Error(`File exceeds the ${this.maxFileSize / (1024 * 1024)}MB limit`);
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }

  isImageFile(file: File): boolean {
    if (file.type.startsWith('image/')) return true;
    const ext = file.name.split('.').pop()?.toLowerCase();
    return ext === 'jpg' || ext === 'jpeg' || ext === 'png' || ext === 'webp';
  }

  getImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        resolve({
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  }
}

export const imageUploadService = new ImageUploadService();
